import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, CircularProgress, Tabs, Tab,
  ToggleButton, ToggleButtonGroup, Button, Alert, Skeleton, Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, Cell,
} from 'recharts';
import api, {
  getStockSnapshot, getTechnicalIndicators, getTechnicalChartData,
  getChartAnnotations, ChartAnnotations,
  getStockResearch, getStockFinancials, getFinvizNews,
  getEarningsHistory, getStockDividends, getEarningsEstimates,
  getNewsFeed, NewsArticleDB,
  FinvizSnapshot, TechnicalChartResponse, StockResearchResponse,
  EarningsHistoryResponse, DividendResponse, EarningsEstimates,
  getAnalystTargets, FinvizAnalystTarget,
  getDcf, getMultiplesTarget, DcfResult, MultiplesTargetResult,
} from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';
import { TickerLink } from '../components/TickerActionMenu';
import ProChart from '../components/ProChart';
import ValuationTab from '../components/ValuationTab';
import StockPulse from '../components/StockPulse';
import { useIsMobile } from '../hooks/useIsMobile';

// ── Helpers ──────────────────────────────────────────────────────────────────

function parsePct(s: string | undefined): number {
  if (!s || s === '-') return 0;
  return parseFloat(s.replace('%', '').replace(',', '')) || 0;
}

function parseNum(s: string | undefined): number {
  if (!s || s === '-') return 0;
  return parseFloat(s.replace(/[,%$B]/g, '')) || 0;
}

function fmtNum(n: number | undefined | null, dec = 2): string {
  if (n == null || isNaN(n as number)) return '—';
  return n.toFixed(dec);
}

function perfColor(v: number): string {
  return v > 0 ? '#22c55e' : v < 0 ? '#ef4444' : '#94a3b8';
}

function fmtPerf(v: number): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function getCurrencySymbol(currency: string | undefined): string {
  const map: Record<string, string> = {
    USD: '$', EUR: '€', GBP: '£', GBp: 'p', GBX: 'p',
    JPY: '¥', CNY: '¥', HKD: 'HK$', CAD: 'C$', AUD: 'A$',
    NZD: 'NZ$', CHF: 'CHF ', SEK: 'kr', NOK: 'kr', DKK: 'kr',
    SGD: 'S$', BRL: 'R$', INR: '₹', KRW: '₩', ZAR: 'R', MXN: '$', TWD: 'NT$',
  };
  return map[currency ?? 'USD'] ?? currency ?? '$';
}

function radarScores(snap: FinvizSnapshot) {
  const perfW = parsePct(snap['Perf Week']);
  const perfM = parsePct(snap['Perf Month']);
  const perfQ = parsePct(snap['Perf Quarter']);
  const momentum = Math.round(clamp01(((perfW + perfM + perfQ) / 3 + 20) / 40) * 100);

  const pe = parseNum(snap['P/E']);
  const value = Math.round(pe <= 0 ? 30 : clamp01(1 - (pe - 5) / 45) * 100);

  const perfY = parsePct(snap['Perf Year']);
  const perfYTD = parsePct(snap['Perf YTD']);
  const growth = Math.round(clamp01(((perfY + perfYTD) / 2 + 30) / 90) * 100);

  const roe = parsePct(snap['ROE']);
  const profM = parsePct(snap['Profit Margin']);
  const debtEq = parseNum(snap['Debt/Eq']);
  const health = Math.round(
    (clamp01(roe / 30) + clamp01((profM + 10) / 40) + clamp01(1 - debtEq / 2)) / 3 * 100
  );

  const recom = parseNum(snap['Recom']) || 3;
  const analysts = Math.round(clamp01(1 - (recom - 1) / 4) * 100);

  return [
    { subject: 'Momentum', score: momentum },
    { subject: 'Value', score: value },
    { subject: 'Growth', score: growth },
    { subject: 'Health', score: health },
    { subject: 'Analysts', score: analysts },
  ];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SIGNAL_LABELS: Record<string, string> = {
  golden_cross: 'Golden Cross', death_cross: 'Death Cross',
  rsi_oversold: 'RSI Oversold', rsi_overbought: 'RSI Overbought',
  macd_bullish: 'MACD Bullish', macd_bearish: 'MACD Bearish',
  breakout_52w_high: '52W High Breakout', breakdown_52w_low: '52W Low Breakdown',
  earnings_beat: 'Earnings Beat', earnings_miss: 'Earnings Miss',
  high_momentum: 'High Momentum', low_momentum: 'Low Momentum',
  volume_spike: 'Volume Spike', trend_reversal_up: 'Trend Reversal ↑',
  trend_reversal_down: 'Trend Reversal ↓',
};

const PERF_KEYS = [
  { label: '1D', field: 'Change' },
  { label: '1W', field: 'Perf Week' },
  { label: '1M', field: 'Perf Month' },
  { label: '3M', field: 'Perf Quarter' },
  { label: '6M', field: 'Perf Half Y' },
  { label: 'YTD', field: 'Perf YTD' },
  { label: '1Y', field: 'Perf Year' },
  { label: '3Y', field: 'Perf 3Y' },
];

const TAB_LABELS = ['Overview', 'Chart', 'Financials', 'Valuation', 'Research', 'News'];

const CHART_PERIODS = ['1m', '3m', '6m', '1y', '2y'] as const;
// Annotation lookback per chart period — structure is horizon-relative, but
// short windows keep a ~6M floor so there's enough pivot material to fit lines.
const ANNOTATION_LOOKBACK: Record<string, number> = { '1m': 180, '3m': 180, '6m': 200, '1y': 420, '2y': 850 };
type ChartPeriod = typeof CHART_PERIODS[number];

const CARD = {
  bgcolor: bloomberg.bg.secondary,
  border: `1px solid ${bloomberg.border.primary}`,
  borderRadius: 1,
  p: 2,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function StockDetail() {
  const { symbol = '' } = useParams<{ symbol: string }>();
  const sym = symbol.toUpperCase();
  const navigate = useNavigate();

  // Header
  const [snap, setSnap] = useState<FinvizSnapshot | null>(null);
  const [price, setPrice] = useState<number>(0);
  const [headerLoading, setHeaderLoading] = useState(true);
  const [headerError, setHeaderError] = useState('');

  // Tab tracking
  const [tab, setTab] = useState(0);
  const [tabsVisited, setTabsVisited] = useState<Set<number>>(new Set([0]));

  // Overview — mini chart
  const [overviewChart, setOverviewChart] = useState<TechnicalChartResponse | null>(null);
  const [overviewChartLoading, setOverviewChartLoading] = useState(false);
  const [overviewPeriod, setOverviewPeriod] = useState<ChartPeriod>('3m');

  // Overview — analyst targets
  const [analystTargets, setAnalystTargets] = useState<FinvizAnalystTarget[]>([]);
  const [analystTargetsLoading, setAnalystTargetsLoading] = useState(false);

  // Overview — company profile (description)
  const [profile, setProfile] = useState<any>(null);

  // Overview signals
  const [signals, setSignals] = useState<any[]>([]);
  const [signalsLoading, setSignalsLoading] = useState(false);

  // Chart tab
  const [chartData, setChartData] = useState<TechnicalChartResponse | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('1y');
  const [annotations, setAnnotations] = useState<ChartAnnotations | null>(null);

  // Financials tab (cheap — yfinance only, no AI)
  const [financials, setFinancials] = useState<{ financial_statements: any; trading_symbol: string; financial_symbol: string } | null>(null);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [financialsError, setFinancialsError] = useState('');
  const [fsTab, setFsTab] = useState<'income_statement' | 'balance_sheet' | 'cash_flow'>('income_statement');
  const [fsView, setFsView] = useState<'annual' | 'quarterly'>('annual');

  // Research tab (AI-generated)
  const [research, setResearch] = useState<StockResearchResponse | null>(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchError, setResearchError] = useState('');

  // News
  const [news, setNews] = useState<NewsArticleDB[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);

  // Earnings history
  const [earningsData, setEarningsData] = useState<EarningsHistoryResponse | null>(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  // Earnings estimates
  const [estimatesData, setEstimatesData] = useState<EarningsEstimates | null>(null);

  // Dividends
  const [dividendData, setDividendData] = useState<DividendResponse | null>(null);
  const [dividendLoading, setDividendLoading] = useState(false);

  // Rail — price targets (base-case DCF + multiples, independent of the
  // interactive Valuation tab's slider-driven copies — always fetched so
  // the rail can show them regardless of which tab is open)
  const [dcf, setDcf] = useState<DcfResult | null>(null);
  const [dcfLoading, setDcfLoading] = useState(false);
  const [multiples, setMultiples] = useState<MultiplesTargetResult | null>(null);
  const [multiplesLoading, setMultiplesLoading] = useState(false);

  // Signal strip — condensed sticky bar shows once the full strip scrolls out of view
  // (desktop only — on mobile there's no spare vertical real estate to justify a
  // second sticky summary bar on top of the now-stacked identity rail)
  const signalStripRef = useRef<HTMLDivElement>(null);
  const [showCondensed, setShowCondensed] = useState(false);
  const isMobile = useIsMobile();

  // ── Initial load ──────────────────────────────────────────────────────────

  useEffect(() => {
    setHeaderLoading(true);
    setSnap(null);
    setOverviewChart(null);
    setProfile(null);
    Promise.all([
      getStockSnapshot(sym),
      getTechnicalIndicators([sym]),
    ]).then(([snapData, techArr]) => {
      setSnap(snapData);
      setPrice(techArr[0]?.current_price ?? parseNum(snapData['Price']));
    }).catch(() => {
      setHeaderError('Failed to load stock data');
    }).finally(() => setHeaderLoading(false));
    // Profile loads independently (slower — yfinance)
    api.get(`/api/stock/${sym}/profile`)
      .then(r => setProfile(r.data))
      .catch(() => {});
  }, [sym]);

  // Rail price targets — base-case DCF + multiples target, loaded eagerly
  // (not gated on the Valuation tab) so the rail always has something to show.
  // Backed by the same 15-min server cache the Valuation tab uses, so opening
  // that tab afterwards doesn't re-fetch from yfinance/EDGAR.
  useEffect(() => {
    setDcf(null);
    setMultiples(null);
    setDcfLoading(true);
    setMultiplesLoading(true);
    getDcf(sym).then(setDcf).catch(() => setDcf(null)).finally(() => setDcfLoading(false));
    getMultiplesTarget(sym).then(setMultiples).catch(() => setMultiples(null)).finally(() => setMultiplesLoading(false));
  }, [sym]);

  // Overview mini chart — load eagerly, reload on period change
  useEffect(() => {
    setOverviewChartLoading(true);
    getTechnicalChartData(sym, overviewPeriod)
      .then(d => setOverviewChart(d))
      .catch(() => {})
      .finally(() => setOverviewChartLoading(false));
  }, [sym, overviewPeriod]);

  // Auto-annotations (S/R, trendlines, channels, patterns) — per symbol AND
  // per horizon: the engine re-derives structure at the selected scale.
  useEffect(() => {
    setAnnotations(null);
    getChartAnnotations(sym, ANNOTATION_LOOKBACK[chartPeriod] || 420)
      .then(setAnnotations).catch(() => {});
  }, [sym, chartPeriod]);

  // Load analyst targets when symbol loads
  useEffect(() => {
    setAnalystTargets([]);
    setAnalystTargetsLoading(true);
    getAnalystTargets(sym)
      .then(d => setAnalystTargets(d.targets || []))
      .catch(() => setAnalystTargets([]))
      .finally(() => setAnalystTargetsLoading(false));
  }, [sym]);

  // Load earnings history + dividends when overview loads
  useEffect(() => {
    if (!snap) return;
    setEarningsLoading(true);
    getEarningsHistory(sym)
      .then(d => setEarningsData(d))
      .catch(() => {})
      .finally(() => setEarningsLoading(false));
    setDividendLoading(true);
    getStockDividends(sym)
      .then(d => setDividendData(d))
      .catch(() => {})
      .finally(() => setDividendLoading(false));
    getEarningsEstimates(sym)
      .then(d => setEstimatesData(d))
      .catch(() => {});
  }, [sym, snap]);

  // Load recent signals once snapshot loaded
  useEffect(() => {
    if (!snap) return;
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - 60);
    setSignalsLoading(true);
    api.get('/api/signals/history', {
      params: { symbol: sym, limit: 10, date_from: dateFrom.toISOString().split('T')[0] },
    }).then(r => {
      setSignals(r.data.signals || []);
    }).catch(() => setSignals([]))
      .finally(() => setSignalsLoading(false));
  }, [sym, snap]);

  // Load chart when tab 1 becomes active or period changes
  useEffect(() => {
    if (!tabsVisited.has(1)) return;
    setChartLoading(true);
    getTechnicalChartData(sym, chartPeriod)
      .then(d => setChartData(d))
      .catch(() => {})
      .finally(() => setChartLoading(false));
  }, [sym, chartPeriod, tabsVisited]);

  // Load financials eagerly once the symbol resolves — yfinance only, no AI
  // cost, and the Overview tab's Financials Snapshot card needs it too, not
  // just the Financials tab.
  useEffect(() => {
    setFinancials(null);
    setFinancialsLoading(true);
    setFinancialsError('');
    getStockFinancials(sym)
      .then(d => setFinancials(d))
      .catch(() => setFinancialsError('Financial statements unavailable'))
      .finally(() => setFinancialsLoading(false));
  }, [sym]);

  // Load AI research lazily for tab 3; refresh=true forces a new report
  const loadResearch = useCallback((refresh = false) => {
    if (!refresh && (research || researchLoading)) return;
    setResearchLoading(true);
    setResearchError('');
    getStockResearch(sym, refresh)
      .then(d => setResearch(d))
      .catch(() => setResearchError('Research data unavailable'))
      .finally(() => setResearchLoading(false));
  }, [sym, research, researchLoading]);

  // On Research tab visit (index 4): check cache silently. Load if found, otherwise show generate button.
  useEffect(() => {
    if (!tabsVisited.has(4) || research || researchLoading) return;
    setResearchLoading(true);
    setResearchError('');
    getStockResearch(sym, false, true)
      .then(d => { if (d) setResearch(d); })
      .catch(() => {})
      .finally(() => setResearchLoading(false));
  }, [sym, tabsVisited]);

  // Load news lazily on News tab visit (index 5) — try DB feed first, fallback to Finviz
  useEffect(() => {
    if (!tabsVisited.has(5) || news.length > 0 || newsLoading) return;
    setNewsLoading(true);
    getNewsFeed({ symbol: sym, page_size: 30 })
      .then(resp => {
        if (resp.articles && resp.articles.length > 0) {
          setNews(resp.articles);
        } else {
          // Fallback to Finviz
          return getFinvizNews(sym).then(d => {
            const finvizArticles: NewsArticleDB[] = (d.news || []).map((item: any, i: number) => ({
              id: i,
              url: item.link || '',
              title: item.title || '',
              source: item.source || 'Finviz',
              published_at: item.date || new Date().toISOString(),
              sentiment: 'neutral',
              is_read: false,
              scraped_at: new Date().toISOString(),
              related_tickers: [],
            }));
            setNews(finvizArticles);
          });
        }
      })
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, [sym, tabsVisited]);

  const handleTabChange = (_: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
    setTabsVisited(prev => new Set(Array.from(prev).concat([newTab])));
  };

  // Reveal the condensed sticky bar once the full signal strip scrolls out of view
  useEffect(() => {
    const el = signalStripRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setShowCondensed(!entry.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [snap]);

  // ── Derived header values ─────────────────────────────────────────────────

  const changePct = parsePct(snap?.['Change']);
  const high52 = parseNum(snap?.['52W High']);
  const low52 = parseNum(snap?.['52W Low']);
  const currencySymbol = getCurrencySymbol(snap?.['Currency']);
  const rangePct = high52 > low52 && price > 0
    ? clamp01((price - low52) / (high52 - low52)) * 100 : null;

  const companyName = snap?.['Company'] || snap?.['Name'] || sym;
  const sector = snap?.['Sector'] || '';
  const country = snap?.['Country'] || '';
  const exchange = snap?.['Exchange'] || '';

  const streetTarget = parseNum(snap?.['Target Price']);
  const streetUpside = streetTarget > 0 && price > 0 ? ((streetTarget - price) / price) * 100 : null;
  const dcfUpside = dcf && !dcf.needs_target_margin && price > 0 ? ((dcf.fair_value_per_share - price) / price) * 100 : null;

  // ── Signal strip ── deliberately distinct from Health Radar's 0-100 scores
  // below (same underlying stats, different lens: raw/interpretable readouts
  // here, abstracted scoring there) so the two don't just repeat each other.
  const sma20 = parsePct(snap?.['SMA20']);
  const sma50 = parsePct(snap?.['SMA50']);
  const sma200 = parsePct(snap?.['SMA200']);
  const smaVotes = [sma20, sma50, sma200].filter(v => v !== 0);
  const bullVotes = smaVotes.filter(v => v > 0).length;
  const technicalTrend = smaVotes.length === 0 ? null
    : bullVotes === smaVotes.length ? 'Bullish' : bullVotes === 0 ? 'Bearish' : 'Neutral';
  const technicalColor = technicalTrend === 'Bullish' ? '#22c55e' : technicalTrend === 'Bearish' ? '#ef4444' : bloomberg.accent.orange;

  const rsi = parseNum(snap?.['RSI (14)']);
  const rsiLabel = !rsi ? null : rsi >= 70 ? 'Overbought' : rsi <= 30 ? 'Oversold' : 'Neutral';

  const recomNum = parseNum(snap?.['Recom']);
  const analystLabel = !recomNum ? null
    : recomNum <= 1.5 ? 'Strong Buy' : recomNum <= 2.5 ? 'Buy' : recomNum <= 3.5 ? 'Hold' : recomNum <= 4.5 ? 'Sell' : 'Strong Sell';
  const analystColor = !recomNum ? bloomberg.text.muted : recomNum <= 2.5 ? '#22c55e' : recomNum <= 3.5 ? '#f59e0b' : '#ef4444';

  const valuationLabel = (dcfUpside == null || streetUpside == null) ? null
    : (dcfUpside >= 0 && streetUpside >= 0) ? 'Undervalued'
    : (dcfUpside < 0 && streetUpside < 0) ? 'Overvalued'
    : 'Mixed signal';
  const valuationColor = valuationLabel === 'Undervalued' ? '#22c55e' : valuationLabel === 'Overvalued' ? '#ef4444' : bloomberg.accent.orange;

  const signalBadges = [
    { label: 'Technical', value: technicalTrend ?? '—', color: technicalColor },
    { label: 'Momentum RSI', value: rsi ? `${rsi.toFixed(1)} · ${rsiLabel}` : '—', color: bloomberg.accent.blue },
    {
      label: 'Analyst View',
      value: analystLabel ? `${analystLabel}${snap?.['Analyst Count'] && snap['Analyst Count'] !== '-' ? ` · ${snap['Analyst Count']}` : ''}` : '—',
      color: analystColor,
    },
    { label: 'Valuation', value: valuationLabel ?? '—', color: valuationColor },
  ];

  // ── Loading / error states ────────────────────────────────────────────────

  if (headerLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (headerError) {
    return <Alert severity="error" sx={{ m: 3 }}>{headerError} ({sym})</Alert>;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', px: 2, pb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>

      {/* ── Persistent identity + price-targets rail — full width and
          non-sticky on mobile, since a sticky 252px rail makes no sense
          when it IS the full width. ── */}
      <Box sx={{ width: { xs: '100%', md: 252 }, flexShrink: 0, position: { xs: 'static', md: 'sticky' }, top: { md: 52 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Identity */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <Chip
              label={sym}
              sx={{ bgcolor: bloomberg.accent.blue, color: '#fff', fontWeight: 800, fontSize: '0.78rem', height: 24, borderRadius: '4px' }}
            />
            {exchange && <Chip label={exchange} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3, mb: 0.5 }}>{companyName}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
            {sector && <Chip label={sector} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', color: bloomberg.accent.blue, borderColor: bloomberg.accent.blue }} />}
            {country && <Chip label={country} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1 }}>
            <Typography sx={{ fontSize: '1.7rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {currencySymbol}{price > 0 ? price.toFixed(2) : '—'}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: perfColor(changePct) }}>
              {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
            </Typography>
          </Box>
          {rangePct != null && (
            <Box sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography sx={{ fontSize: '0.62rem', color: bloomberg.text.muted }}>${fmtNum(low52)}</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: bloomberg.text.muted }}>${fmtNum(high52)}</Typography>
              </Box>
              <Box sx={{ position: 'relative', height: 5, bgcolor: bloomberg.bg.tertiary, borderRadius: 3 }}>
                <Box sx={{ height: '100%', width: `${rangePct}%`, bgcolor: `${bloomberg.accent.orange}55`, borderRadius: 3 }} />
                <Box sx={{
                  position: 'absolute', left: `${rangePct}%`, top: '50%', transform: 'translate(-50%, -50%)',
                  width: 10, height: 10, borderRadius: '50%', bgcolor: bloomberg.accent.orange, border: '2px solid #000', zIndex: 1,
                }} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Key stats — vertical list, no wrapping */}
        <Box sx={{ bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, px: 1.5, py: 0.5 }}>
          {[
            { label: 'Mkt Cap', val: snap?.['Market Cap'] },
            { label: 'P/E', val: snap?.['P/E'] },
            { label: 'Div Yield', val: snap?.['Dividend %'] },
            { label: 'Beta', val: snap?.['Beta'] },
            { label: 'Avg Vol', val: snap?.['Avg Volume'] },
          ].filter(({ val }) => val && val !== '-').map(({ label, val }, i, arr) => (
            <Box key={label} sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6,
              borderBottom: i < arr.length - 1 ? `1px solid ${bloomberg.border.primary}` : 'none',
            }}>
              <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.muted }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{val}</Typography>
            </Box>
          ))}
        </Box>

        {/* Price targets — DCF / Multiples / Street, always visible */}
        <Box sx={{ bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, p: 1.5 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
            Price Targets
          </Typography>
          {(dcfLoading || multiplesLoading) && !dcf && !multiples ? (
            <CircularProgress size={16} sx={{ color: bloomberg.accent.orange }} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.secondary }}>DCF Fair Value</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: dcfUpside != null ? perfColor(dcfUpside) : 'inherit' }}>
                    {dcf && !dcf.needs_target_margin ? `${currencySymbol}${dcf.fair_value_per_share.toFixed(2)}` : '—'}
                  </Typography>
                </Box>
                {dcfUpside != null && (
                  <Typography sx={{ fontSize: '0.65rem', color: perfColor(dcfUpside), textAlign: 'right' }}>{fmtPerf(dcfUpside)}</Typography>
                )}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.secondary }}>Multiples Target</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: multiples?.blended_upside != null ? perfColor(multiples.blended_upside) : 'inherit' }}>
                    {multiples?.blended_target != null ? `${currencySymbol}${multiples.blended_target.toFixed(2)}` : '—'}
                  </Typography>
                </Box>
                {multiples?.blended_upside != null && (
                  <Typography sx={{ fontSize: '0.65rem', color: perfColor(multiples.blended_upside), textAlign: 'right' }}>{fmtPerf(multiples.blended_upside)}</Typography>
                )}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.secondary }}>Street Consensus</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: streetUpside != null ? perfColor(streetUpside) : 'inherit' }}>
                    {streetTarget > 0 ? `${currencySymbol}${streetTarget.toFixed(2)}` : '—'}
                  </Typography>
                </Box>
                {streetUpside != null && (
                  <Typography sx={{ fontSize: '0.65rem', color: perfColor(streetUpside), textAlign: 'right' }}>{fmtPerf(streetUpside)}</Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Button
          size="small" variant="outlined" fullWidth
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => navigate(`/research?symbol=${sym}`)}
          sx={{ fontSize: '0.72rem' }}
        >
          Full Research
        </Button>
      </Box>

      {/* ── Right pane ────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* Condensed sticky bar — appears once the signal strip scrolls away (desktop only) */}
        <Box sx={{
          position: 'sticky', top: 40, zIndex: 5, display: (showCondensed && !isMobile) ? 'flex' : 'none',
          alignItems: 'center', gap: 1.5, bgcolor: bloomberg.bg.primary,
          borderBottom: `1px solid ${bloomberg.border.primary}`, py: 1, mb: (showCondensed && !isMobile) ? 1 : 0,
        }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: bloomberg.accent.orange, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {TAB_LABELS[tab]}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto', flexWrap: 'wrap' }}>
            {signalBadges.map(({ label, value, color }) => (
              <Chip
                key={label}
                label={value}
                size="small"
                sx={{
                  height: 20, fontSize: '0.62rem', fontWeight: 700,
                  bgcolor: bloomberg.bg.tertiary, color,
                  borderLeft: `2px solid ${color}`, borderRadius: '3px',
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: `1px solid ${bloomberg.border.primary}`, mb: 2 }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Chart" />
            <Tab label="Financials" />
            <Tab label="Valuation" />
            <Tab label="Research" />
            <Tab label="News" />
          </Tabs>
        </Box>

        {/* Signal strip — raw/interpretable readouts (trend, RSI, analyst
            label, valuation gap), deliberately distinct from Health Radar's
            abstracted 0-100 scores further down so they don't just repeat
            each other. */}
        {snap && (
          <Box ref={signalStripRef} sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1, mb: 2 }}>
            {signalBadges.map(({ label, value, color }) => (
              <Box key={label} sx={{
                bgcolor: bloomberg.bg.secondary, borderLeft: `3px solid ${color}`,
                borderRadius: 1, px: 1.25, py: 0.75,
              }}>
                <Typography sx={{ fontSize: '0.65rem', color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Tab 0 — Overview                                                    */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {tab === 0 && snap && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ── Price Chart + Analyst Consensus: two columns ─────────────────── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>

            {/* Price Chart */}
            <Box sx={CARD}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1">Price Chart</Typography>
                <ToggleButtonGroup
                  value={overviewPeriod} exclusive size="small"
                  onChange={(_, v) => v && setOverviewPeriod(v)}
                >
                  {(['1m', '3m', '6m', '1y', '2y'] as ChartPeriod[]).map(p => (
                    <ToggleButton key={p} value={p} sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}>
                      {p.toUpperCase()}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              {overviewChartLoading ? (
                <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : overviewChart ? (() => {
                const d = overviewChart.data;
                const isUp = d.length > 1 ? d[d.length - 1].price >= d[0].price : true;
                const lineColor = isUp ? '#22c55e' : '#ef4444';
                return (
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={d} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                      <defs>
                        <linearGradient id="ovGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={lineColor} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fill: bloomberg.text.muted, fontSize: 9 }}
                        tickFormatter={d => d.slice(5)} interval={Math.max(1, Math.floor(d.length / 5))} axisLine={false} tickLine={false} />
                      <YAxis domain={['auto', 'auto']} tick={{ fill: bloomberg.text.muted, fontSize: 9 }} tickCount={4}
                        tickFormatter={v => `${currencySymbol}${Number(v).toFixed(0)}`} axisLine={false} tickLine={false} width={45} />
                      <ReTooltip
                        contentStyle={{ background: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.secondary}`, fontSize: '0.75rem' }}
                        formatter={(val: any) => [`${currencySymbol}${Number(val).toFixed(2)}`, 'Price']}
                        labelFormatter={l => l}
                      />
                      <Area dataKey="price" stroke={lineColor} strokeWidth={2} fill="url(#ovGrad)" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                );
              })() : null}
            </Box>

            {/* Analyst Consensus — prominent */}
            <Box sx={CARD}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Analyst Consensus</Typography>
              {snap['Recom'] && snap['Recom'] !== '-' ? (() => {
                const recom = parseNum(snap['Recom']);
                const label = recom <= 1.5 ? 'Strong Buy' : recom <= 2.5 ? 'Buy' : recom <= 3.5 ? 'Hold' : recom <= 4.5 ? 'Sell' : 'Strong Sell';
                const color = recom <= 2.5 ? '#22c55e' : recom <= 3.5 ? '#f59e0b' : '#ef4444';
                return (
                  <Box>
                    {/* Big label — numeric score now lives in the Signal Strip above,
                        price target/upside now lives in the rail; avoid repeating both here */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color }}>{label}</Typography>
                      {snap['Analyst Count'] && snap['Analyst Count'] !== '-' && (
                        <Typography sx={{ fontSize: '0.78rem', color: bloomberg.text.muted, ml: 'auto' }}>
                          {snap['Analyst Count']} analyst{snap['Analyst Count'] === '1' ? '' : 's'}
                        </Typography>
                      )}
                    </Box>
                    {/* Earnings Estimates */}
                    {(snap['Earnings'] && snap['Earnings'] !== '-') || estimatesData ? (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${bloomberg.border.primary}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontWeight: 700, fontSize: '0.75rem' }}>Next Earnings</Typography>
                          {(estimatesData?.earnings_date || snap['Earnings']) && (
                            <Chip label={estimatesData?.earnings_date || snap['Earnings']} size="small"
                              sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22, bgcolor: bloomberg.accent.blue + '22', color: bloomberg.accent.blue }} />
                          )}
                        </Box>
                        {estimatesData?.eps_estimate != null && (
                          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ color: bloomberg.text.muted, display: 'block', lineHeight: 1.2 }}>EPS Estimate</Typography>
                              <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}>
                                {currencySymbol}{estimatesData.eps_estimate.toFixed(2)}
                              </Typography>
                              {estimatesData.eps_low != null && estimatesData.eps_high != null && (
                                <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontSize: '0.7rem' }}>
                                  Range: {currencySymbol}{estimatesData.eps_low.toFixed(2)} – {currencySymbol}{estimatesData.eps_high.toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                            {estimatesData.revenue_estimate != null && (
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ color: bloomberg.text.muted, display: 'block', lineHeight: 1.2 }}>Revenue Est.</Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}>
                                  {estimatesData.revenue_estimate >= 1e9
                                    ? `$${(estimatesData.revenue_estimate / 1e9).toFixed(1)}B`
                                    : estimatesData.revenue_estimate >= 1e6
                                      ? `$${(estimatesData.revenue_estimate / 1e6).toFixed(0)}M`
                                      : `$${estimatesData.revenue_estimate.toLocaleString()}`}
                                </Typography>
                                {estimatesData.revenue_low != null && estimatesData.revenue_high != null && (
                                  <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontSize: '0.7rem' }}>
                                    {estimatesData.revenue_low >= 1e9
                                      ? `$${(estimatesData.revenue_low / 1e9).toFixed(1)}B`
                                      : `$${(estimatesData.revenue_low / 1e6).toFixed(0)}M`}
                                    {' – '}
                                    {estimatesData.revenue_high >= 1e9
                                      ? `$${(estimatesData.revenue_high / 1e9).toFixed(1)}B`
                                      : `$${(estimatesData.revenue_high / 1e6).toFixed(0)}M`}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                        {/* Last quarter result */}
                        {estimatesData?.last_quarter_surprise_pct != null && (
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
                            bgcolor: bloomberg.bg.tertiary, borderRadius: 1, px: 1.5, py: 0.75,
                          }}>
                            <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Last Q:</Typography>
                            {estimatesData.last_quarter_eps_actual != null && (
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                {currencySymbol}{estimatesData.last_quarter_eps_actual.toFixed(2)}
                              </Typography>
                            )}
                            {estimatesData.last_quarter_eps_estimate != null && (
                              <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>
                                vs est. {currencySymbol}{estimatesData.last_quarter_eps_estimate.toFixed(2)}
                              </Typography>
                            )}
                            <Chip
                              label={`${estimatesData.last_quarter_surprise_pct >= 0 ? '+' : ''}${estimatesData.last_quarter_surprise_pct.toFixed(1)}%`}
                              size="small"
                              sx={{
                                fontWeight: 700, fontSize: '0.7rem', height: 20, ml: 'auto',
                                bgcolor: estimatesData.last_quarter_surprise_pct >= 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                color: estimatesData.last_quarter_surprise_pct >= 0 ? '#22c55e' : '#ef4444',
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    ) : null}
                  </Box>
                );
              })() : (
                <Typography sx={{ color: bloomberg.text.muted, fontSize: '0.85rem' }}>No consensus data</Typography>
              )}
            </Box>
          </Box>

          {/* ── Performance + Technicals ──────────────────────────────────── */}
          <Box sx={CARD}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Performance</Typography>

            {/* Horizontal bar chart */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {PERF_KEYS.map(({ label, field }) => {
                const v = parsePct(snap[field]);
                const maxBar = label === '3Y' ? 200 : 30;
                const barPct = Math.min(Math.abs(v) / maxBar * 100, 100);
                const isPos = v >= 0;
                return (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: bloomberg.text.muted, width: 28, textAlign: 'right', flexShrink: 0 }}>
                      {label}
                    </Typography>
                    <Box sx={{ display: 'flex', flex: 1, height: 20, borderRadius: 0.5, overflow: 'hidden' }}>
                      {/* Left half (negative) */}
                      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', bgcolor: bloomberg.bg.tertiary }}>
                        {!isPos && v !== 0 && (
                          <Box sx={{
                            width: `${barPct}%`, height: '100%',
                            bgcolor: '#ef4444',
                            borderRadius: '3px 0 0 3px',
                            transition: 'width 0.3s ease',
                          }} />
                        )}
                      </Box>
                      {/* Center divider */}
                      <Box sx={{ width: 2, bgcolor: bloomberg.border.secondary, flexShrink: 0 }} />
                      {/* Right half (positive) */}
                      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', bgcolor: bloomberg.bg.tertiary }}>
                        {isPos && v !== 0 && (
                          <Box sx={{
                            width: `${barPct}%`, height: '100%',
                            bgcolor: '#22c55e',
                            borderRadius: '0 3px 3px 0',
                            transition: 'width 0.3s ease',
                          }} />
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: perfColor(v), fontVariantNumeric: 'tabular-nums', width: 58, textAlign: 'right', flexShrink: 0 }}>
                      {fmtPerf(v)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* SMA Distances + Technicals */}
            {(() => {
              const sma20 = parsePct(snap['SMA20']);
              const sma50 = parsePct(snap['SMA50']);
              const sma200 = parsePct(snap['SMA200']);
              const volat = snap['Volatility'] && snap['Volatility'] !== '-' ? snap['Volatility'] : null;
              const hasSMA = sma20 !== 0 || sma50 !== 0 || sma200 !== 0;

              return (hasSMA || volat) ? (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${bloomberg.border.primary}` }}>
                  {/* SMA distances */}
                  {hasSMA && (
                    <Box sx={{ display: 'flex', gap: 1.5, mb: volat ? 1.5 : 0, flexWrap: 'wrap' }}>
                      {[
                        { label: 'SMA 20', val: sma20 },
                        { label: 'SMA 50', val: sma50 },
                        { label: 'SMA 200', val: sma200 },
                      ].filter(s => s.val !== 0).map(({ label, val }) => (
                        <Box key={label} sx={{
                          flex: '1 1 0', textAlign: 'center', py: 0.75, px: 1,
                          bgcolor: bloomberg.bg.tertiary, borderRadius: 1,
                          borderLeft: `3px solid ${val > 0 ? '#22c55e' : '#ef4444'}`,
                        }}>
                          <Typography variant="caption" sx={{ color: bloomberg.text.muted, display: 'block', lineHeight: 1.2 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: perfColor(val), fontVariantNumeric: 'tabular-nums' }}>
                            {val > 0 ? '+' : ''}{val.toFixed(2)}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Volatility */}
                  {volat && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 0', textAlign: 'center', py: 0.75, px: 1, bgcolor: bloomberg.bg.tertiary, borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: bloomberg.text.muted, display: 'block', lineHeight: 1.2 }}>Volatility</Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{volat}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : null;
            })()}
          </Box>

          {/* ── Financials Snapshot ───────────────────────────────────────── */}
          <Box sx={CARD}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">Financials Snapshot</Typography>
              <Typography
                onClick={() => { setTab(2); setTabsVisited(prev => new Set(Array.from(prev).concat([2]))); }}
                sx={{ fontSize: '0.72rem', color: bloomberg.accent.blue, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Full statements →
              </Typography>
            </Box>
            {financialsLoading ? (
              <CircularProgress size={18} />
            ) : (() => {
              const ann = financials?.financial_statements?.income_statement?.annual;
              const rows = ann?.rows || [];
              const period = ann?.periods?.[0];
              const getRow = (metric: string) => rows.find((r: any) => r.metric === metric);
              const items = ['Total Revenue', 'Net Income', 'Diluted EPS', 'EBITDA']
                .map(metric => {
                  const row = getRow(metric);
                  const val = row?.values?.[0];
                  const curr = row?.raw_values?.[0];
                  const prev = row?.raw_values?.[1];
                  const yoy = (curr != null && prev != null && prev !== 0) ? ((curr - prev) / Math.abs(prev)) * 100 : null;
                  return { label: metric, val, yoy };
                })
                .filter(({ val }) => val != null);
              return items.length === 0 ? (
                <Typography sx={{ color: bloomberg.text.muted, fontSize: '0.85rem' }}>Financial statements unavailable</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th style={{
                          textAlign: 'left', padding: '8px 12px',
                          background: bloomberg.bg.tertiary, color: bloomberg.accent.orange,
                          fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                          letterSpacing: '0.05em', borderBottom: `2px solid ${bloomberg.accent.orange}`,
                        }}>Line Item</th>
                        <th style={{
                          textAlign: 'right', padding: '8px 12px',
                          background: bloomberg.bg.tertiary, color: bloomberg.accent.orange,
                          fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                          letterSpacing: '0.05em', borderBottom: `2px solid ${bloomberg.accent.orange}`,
                        }}>{period || 'Latest'}</th>
                        <th style={{
                          textAlign: 'right', padding: '8px 12px',
                          background: bloomberg.bg.tertiary, color: bloomberg.accent.orange,
                          fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                          letterSpacing: '0.05em', borderBottom: `2px solid ${bloomberg.accent.orange}`,
                        }}>YoY</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(({ label, val, yoy }, i) => (
                        <tr key={label} style={{ background: i % 2 === 0 ? bloomberg.bg.primary : bloomberg.bg.secondary }}>
                          <td style={{
                            padding: '6px 12px', color: bloomberg.text.secondary, fontWeight: 500,
                            borderBottom: `1px solid ${bloomberg.border.primary}`,
                          }}>{label}</td>
                          <td style={{
                            textAlign: 'right', padding: '6px 12px', fontVariantNumeric: 'tabular-nums',
                            fontFamily: '"IBM Plex Mono", "Fira Code", monospace', fontWeight: 700, color: bloomberg.text.primary,
                            borderBottom: `1px solid ${bloomberg.border.primary}`,
                          }}>{val}</td>
                          <td style={{
                            textAlign: 'right', padding: '6px 12px', fontVariantNumeric: 'tabular-nums',
                            fontFamily: '"IBM Plex Mono", "Fira Code", monospace', fontWeight: 700,
                            color: yoy != null ? perfColor(yoy) : bloomberg.text.muted,
                            borderBottom: `1px solid ${bloomberg.border.primary}`,
                          }}>
                            {yoy != null ? fmtPerf(yoy) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              );
            })()}
          </Box>

          {/* ── Analyst Ratings table ────────────────────────────────────── */}
          {(analystTargetsLoading || analystTargets.length > 0) && (
            <Box sx={{ ...CARD, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Analyst Ratings</Typography>
              {analystTargetsLoading ? (
                <Typography sx={{ fontSize: '0.8rem', color: bloomberg.text.muted }}>Loading…</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                        {['Date', 'Firm', 'Action', 'Rating', 'From', 'To'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '4px 10px 6px', fontWeight: 600, color: bloomberg.text.muted, whiteSpace: 'nowrap', fontSize: 11 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analystTargets.map((t, i) => {
                        const cat = t.category?.toLowerCase() ?? '';
                        const actionColor =
                          cat === 'upgrade'   ? '#4caf50' :
                          cat === 'downgrade' ? '#ef5350' :
                          cat === 'initiated' ? '#90caf9' : bloomberg.text.muted;
                        return (
                          <tr key={i} style={{ borderBottom: `1px solid ${bloomberg.border.primary}`, background: i % 2 === 0 ? 'transparent' : bloomberg.bg.secondary }}>
                            <td style={{ padding: '5px 10px', color: bloomberg.text.muted, whiteSpace: 'nowrap' }}>{t.date}</td>
                            <td style={{ padding: '5px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{t.analyst}</td>
                            <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                              <span style={{ color: actionColor, fontWeight: 600, fontSize: 11 }}>{t.category}</span>
                            </td>
                            <td style={{ padding: '5px 10px' }}>{t.rating}</td>
                            <td style={{ padding: '5px 10px', color: bloomberg.text.muted, whiteSpace: 'nowrap' }}>
                              {t.target_from != null ? `${currencySymbol}${t.target_from.toFixed(0)}` : '—'}
                            </td>
                            <td style={{ padding: '5px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {t.target_to != null ? `${currencySymbol}${t.target_to.toFixed(0)}` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          )}

          {/* ── Key Ratios + Health Radar: two columns ─────────────────────── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>

            {/* Key Ratios */}
            <Box sx={CARD}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Key Ratios</Typography>
              {[
                ['P/E', snap['P/E']], ['Fwd P/E', snap['Forward P/E']],
                ['P/B', snap['P/B']], ['P/S', snap['P/S']],
                ['EV/EBITDA', snap['EV/EBITDA']], ['ROE', snap['ROE']],
                ['Debt/Eq', snap['Debt/Eq']], ['Profit Margin', snap['Profit Margin']],
                ['Gross Margin', snap['Gross Margin']], ['Oper. Margin', snap['Oper. Margin']],
              ].map(([label, val]) => (
                <Box key={String(label)} sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  py: 0.65, borderBottom: `1px solid ${bloomberg.border.primary}`,
                  '&:last-child': { borderBottom: 'none' },
                }}>
                  <Typography sx={{ fontSize: '0.8rem', color: bloomberg.text.secondary }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: val && val !== '-' && val.startsWith('-') && !val.startsWith('--') ? '#ef4444' : 'inherit' }}>
                    {(!val || val === '-') ? '—' : val}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Health Radar */}
            <Box sx={CARD}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Typography variant="subtitle1">Health Radar</Typography>
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <Box sx={{ p: 0.5, maxWidth: 300 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 1 }}>Score Methodology (0–100)</Typography>
                      {[
                        { name: 'Momentum', color: '#f97316', desc: 'Average of 1W, 1M and 3M price performance. Centred at 0%, scaled so +20% avg = 100 and −20% avg = 0.' },
                        { name: 'Value', color: '#22c55e', desc: 'Based on trailing P/E ratio. P/E ≤ 5 scores 100; P/E = 50 scores 0. Negative or missing P/E scores 30 (no-earnings penalty).' },
                        { name: 'Growth', color: '#3b82f6', desc: 'Average of 1Y and YTD price performance. Centred at 0%, scaled so +60% avg = 100 and −30% avg = 0.' },
                        { name: 'Health', color: '#a855f7', desc: 'Equal-weight average of three factors: ROE (30% target = 100), Profit Margin (10% target = 75), and Debt/Equity (0 = 100, 2+ = 0).' },
                        { name: 'Analysts', color: '#ef4444', desc: 'Derived from Finviz consensus rating (1 = Strong Buy → 5 = Strong Sell). Score = (5 − rating) / 4 × 100.' },
                      ].map(({ name, color, desc }) => (
                        <Box key={name} sx={{ mb: 1 }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>{name}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1', lineHeight: 1.4 }}>{desc}</Typography>
                        </Box>
                      ))}
                      <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mt: 0.5 }}>All scores are snapshots based on Finviz data and do not constitute financial advice.</Typography>
                    </Box>
                  }
                >
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: bloomberg.text.muted, cursor: 'help', '&:hover': { color: bloomberg.accent.orange } }} />
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={240}>
                <RadarChart data={radarScores(snap)} outerRadius={90}>
                  <PolarGrid stroke={bloomberg.border.secondary} />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: bloomberg.text.secondary, fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar dataKey="score" stroke={bloomberg.accent.orange} fill={bloomberg.accent.orange} fillOpacity={0.25} />
                </RadarChart>
              </ResponsiveContainer>
              {/* Per-axis score strip */}
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 0.5 }}>
                {radarScores(snap).map(({ subject, score }) => {
                  const color = score >= 70 ? '#22c55e' : score >= 40 ? bloomberg.accent.orange : '#ef4444';
                  return (
                    <Box key={subject} sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: bloomberg.text.muted, lineHeight: 1.2 }}>{subject}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{score}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* ── Company Profile ────────────────────────────────────────────── */}
          <Box sx={CARD}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Company Profile</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 0, mb: 2, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, overflow: 'hidden' }}>
              {[
                { label: 'Sector', val: snap['Sector'] },
                { label: 'Industry', val: snap['Industry'] },
                { label: 'Index', val: snap['Index'] },
                { label: 'Country', val: snap['Country'] },
                { label: 'Employees', val: snap['Employees'] ? Number(snap['Employees'].replace(/,/g,'')).toLocaleString() : null },
                { label: 'IPO Date', val: snap['IPO'] },
                { label: 'Mkt Cap', val: snap['Market Cap'] },
                { label: 'Enterprise Val', val: snap['Enterprise Value'] },
              ].filter(({ val }) => val && val !== '-').map(({ label, val }, i, arr) => (
                <Box key={label} sx={{
                  px: 2, py: 1.25,
                  borderRight: `1px solid ${bloomberg.border.primary}`,
                  borderBottom: `1px solid ${bloomberg.border.primary}`,
                  '&:last-child': { borderRight: 'none' },
                }}>
                  <Typography variant="caption" sx={{ color: bloomberg.text.muted, display: 'block', lineHeight: 1.3 }}>{label}</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{val}</Typography>
                </Box>
              ))}
            </Box>

            {/* Ownership & Short Interest row */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
              {[
                { label: 'Insider Own', val: snap['Insider Own'] },
                { label: 'Inst. Own', val: snap['Inst Own'] },
                { label: 'Short Float', val: snap['Short Float'] },
                { label: 'Short Ratio', val: snap['Short Ratio'] },
              ].filter(({ val }) => val && val !== '-').map(({ label, val }) => (
                <Box key={label}>
                  <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>{label}</Typography>
                  <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '0.9rem' }}>{val}</Typography>
                </Box>
              ))}
              {snap['Website'] && snap['Website'] !== '-' && (
                <Box sx={{ ml: 'auto' }}>
                  <Typography
                    component="a" href={snap['Website']} target="_blank" rel="noopener noreferrer"
                    sx={{ color: bloomberg.accent.blue, fontSize: '0.85rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {snap['Website'].replace(/^https?:\/\//, '')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Description */}
            {profile?.description ? (
              <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.7, color: bloomberg.text.secondary }}>
                {profile.description}
              </Typography>
            ) : !profile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Loading description…</Typography>
              </Box>
            ) : null}
          </Box>

          {/* ── Earnings History ──────────────────────────────────────────── */}
          <Box sx={CARD}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">Earnings History</Typography>
              {earningsData?.beat_rate && (
                <Chip
                  label={`Beat Rate: ${earningsData.beat_rate.rate_str}`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: '0.75rem',
                    bgcolor: earningsData.beat_rate.beats / earningsData.beat_rate.total >= 0.75
                      ? 'rgba(34,197,94,0.15)' : earningsData.beat_rate.beats / earningsData.beat_rate.total >= 0.5
                      ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                    color: earningsData.beat_rate.beats / earningsData.beat_rate.total >= 0.75
                      ? '#22c55e' : earningsData.beat_rate.beats / earningsData.beat_rate.total >= 0.5
                      ? '#f59e0b' : '#ef4444',
                  }}
                />
              )}
            </Box>
            {earningsLoading ? <CircularProgress size={20} /> : !earningsData?.history?.length ? (
              <Typography sx={{ color: bloomberg.text.muted, fontSize: '0.85rem' }}>No earnings data available</Typography>
            ) : (
              <>
                {/* Surprise % bar chart */}
                {earningsData.history.filter(q => q.eps_surprise_pct != null).length > 1 && (
                  <Box sx={{ height: 200, mb: 1.5 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[...earningsData.history].reverse().filter(q => q.eps_surprise_pct != null)}>
                        <XAxis dataKey="report_date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 10, fill: bloomberg.text.muted }} />
                        <YAxis tick={{ fontSize: 10, fill: bloomberg.text.muted }} tickFormatter={v => `${v}%`} />
                        <ReTooltip
                          formatter={(v: number) => [`${v.toFixed(2)}%`, 'Surprise']}
                          labelFormatter={d => `Report: ${d}`}
                          contentStyle={{ background: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, fontSize: 12 }}
                        />
                        <Bar dataKey="eps_surprise_pct" name="EPS Surprise %" radius={[3, 3, 0, 0]}>
                          {[...earningsData.history].reverse().filter(q => q.eps_surprise_pct != null).map((q, i) => (
                            <Cell key={i} fill={(q.eps_surprise_pct ?? 0) >= 0 ? '#22c55e' : '#ef4444'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
                {/* Quarter-by-quarter table */}
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                        <th style={{ textAlign: 'left', padding: '4px 8px', color: bloomberg.text.secondary, fontWeight: 600 }}>Date</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', color: bloomberg.text.secondary, fontWeight: 600 }}>EPS Est</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', color: bloomberg.text.secondary, fontWeight: 600 }}>EPS Act</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', color: bloomberg.text.secondary, fontWeight: 600 }}>Surprise</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', color: bloomberg.text.secondary, fontWeight: 600 }}>Post Move</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earningsData.history.map((q, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                          <td style={{ padding: '5px 8px', fontVariantNumeric: 'tabular-nums' }}>{q.report_date}</td>
                          <td style={{ textAlign: 'right', padding: '5px 8px' }}>{q.eps_estimate != null ? `$${q.eps_estimate.toFixed(2)}` : '—'}</td>
                          <td style={{ textAlign: 'right', padding: '5px 8px', fontWeight: 600 }}>{q.eps_actual != null ? `$${q.eps_actual.toFixed(2)}` : '—'}</td>
                          <td style={{
                            textAlign: 'right', padding: '5px 8px', fontWeight: 700,
                            color: q.eps_surprise_pct == null ? bloomberg.text.muted : q.eps_surprise_pct >= 0 ? '#22c55e' : '#ef4444',
                          }}>
                            {q.eps_surprise_pct != null ? `${q.eps_surprise_pct >= 0 ? '+' : ''}${q.eps_surprise_pct.toFixed(1)}%` : '—'}
                          </td>
                          <td style={{
                            textAlign: 'right', padding: '5px 8px', fontWeight: 600,
                            color: q.post_earnings_move == null ? bloomberg.text.muted : q.post_earnings_move >= 0 ? '#22c55e' : '#ef4444',
                          }}>
                            {q.post_earnings_move != null ? `${q.post_earnings_move >= 0 ? '+' : ''}${q.post_earnings_move.toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </>
            )}
          </Box>

          {/* ── Dividends ────────────────────────────────────────────────────── */}
          {dividendLoading ? null : dividendData && (dividendData.dividend_yield || dividendData.history.length > 0) && (
            <Box sx={CARD}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Dividends</Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: dividendData.history.length > 2 ? 2 : 0 }}>
                {[
                  { label: 'Yield', value: dividendData.dividend_yield != null ? `${(dividendData.dividend_yield * 100).toFixed(2)}%` : '—' },
                  { label: 'Annual Rate', value: dividendData.dividend_rate != null ? `${getCurrencySymbol(dividendData.currency)}${dividendData.dividend_rate.toFixed(2)}` : '—' },
                  { label: 'Payout Ratio', value: dividendData.payout_ratio != null ? `${(dividendData.payout_ratio * 100).toFixed(1)}%` : '—' },
                  { label: 'Ex-Div Date', value: dividendData.ex_dividend_date || '—' },
                  { label: '1Y Growth', value: dividendData.five_year_growth_rate != null ? `${dividendData.five_year_growth_rate >= 0 ? '+' : ''}${dividendData.five_year_growth_rate.toFixed(1)}%` : '—' },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: '0.7rem', color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
              {dividendData.history.length > 2 && (
                <Box sx={{ height: 110 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dividendData.history.slice(-20)}>
                      <XAxis dataKey="date" tickFormatter={d => d.slice(0, 7)} tick={{ fontSize: 10, fill: bloomberg.text.muted }} />
                      <YAxis tick={{ fontSize: 10, fill: bloomberg.text.muted }} />
                      <ReTooltip
                        formatter={(v: number) => [`${getCurrencySymbol(dividendData.currency)}${v.toFixed(4)}`, 'Dividend']}
                        contentStyle={{ background: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, fontSize: 12 }}
                      />
                      <Bar dataKey="amount" fill={bloomberg.accent.orange} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          )}

          {/* ── What's going on (local-model narrative) ─────────────────── */}
          <StockPulse symbol={sym} />

          {/* ── Recent Signals ─────────────────────────────────────────────── */}
          <Box sx={CARD}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Recent Signals (60 days)</Typography>
            {signalsLoading ? (
              <CircularProgress size={20} />
            ) : signals.length === 0 ? (
              <Typography sx={{ color: bloomberg.text.muted, fontSize: '0.85rem' }}>
                No signals in the past 60 days
              </Typography>
            ) : (
              <Box>
                {signals.map((s, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    py: 0.75, borderBottom: `1px solid ${bloomberg.border.primary}`,
                    '&:last-child': { borderBottom: 'none' },
                  }}>
                    <Chip
                      label={s.direction === 'bullish' ? '↑' : s.direction === 'bearish' ? '↓' : '·'}
                      size="small"
                      sx={{
                        width: 28, fontSize: '0.85rem', fontWeight: 700,
                        bgcolor: s.direction === 'bullish' ? 'rgba(34,197,94,0.15)'
                          : s.direction === 'bearish' ? 'rgba(239,68,68,0.15)'
                          : bloomberg.bg.tertiary,
                        color: s.direction === 'bullish' ? '#22c55e'
                          : s.direction === 'bearish' ? '#ef4444'
                          : bloomberg.text.secondary,
                      }}
                    />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, flex: 1 }}>
                      {SIGNAL_LABELS[s.signal_type] || s.signal_type}
                    </Typography>
                    {s.price != null && (
                      <Typography sx={{ fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums', color: bloomberg.text.secondary }}>
                        {currencySymbol}{Number(s.price).toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: bloomberg.text.muted, minWidth: 90, textAlign: 'right' }}>
                      {s.signal_date}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Tab 1 — Chart                                                       */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          {/* Period selector */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <ToggleButtonGroup
              value={chartPeriod} exclusive size="small"
              onChange={(_, v) => v && setChartPeriod(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: '0.7rem' } }}
            >
              {CHART_PERIODS.map(p => (
                <ToggleButton key={p} value={p}>{p.toUpperCase()}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {chartLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
            </Box>
          ) : chartData && chartData.data.length > 0 ? (
            <ProChart
              data={chartData.data}
              annotations={annotations}
              fibonacci={chartData.fibonacci}
              periodHigh={chartData.period_high}
              periodLow={chartData.period_low}
              currencySymbol={currencySymbol}
              height={480}
            />
          ) : (
            <Alert severity="info">No chart data available for {sym}</Alert>
          )}
        </Box>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Tab 2 — Financials                                                  */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          {financialsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : financialsError ? (
            <Alert severity="error">{financialsError}</Alert>
          ) : financials?.financial_statements ? (() => {
            const tbl = financials.financial_statements[fsTab][fsView];
            return (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <ToggleButtonGroup value={fsView} exclusive size="small" onChange={(_, v) => v && setFsView(v)}>
                    <ToggleButton value="annual">Annual</ToggleButton>
                    <ToggleButton value="quarterly">Quarterly</ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup value={fsTab} exclusive size="small" onChange={(_, v) => v && setFsTab(v)}>
                    <ToggleButton value="income_statement">Income</ToggleButton>
                    <ToggleButton value="balance_sheet">Balance Sheet</ToggleButton>
                    <ToggleButton value="cash_flow">Cash Flow</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th style={{
                          textAlign: 'left', padding: '8px 12px',
                          background: bloomberg.bg.tertiary, color: bloomberg.accent.orange,
                          fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                          letterSpacing: '0.05em', position: 'sticky', left: 0,
                          borderBottom: `2px solid ${bloomberg.accent.orange}`,
                        }}>Metric</th>
                        {tbl.periods.map((p: string) => (
                          <th key={p} style={{
                            textAlign: 'right', padding: '8px 12px',
                            background: bloomberg.bg.tertiary, color: bloomberg.accent.orange,
                            fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap',
                            borderBottom: `2px solid ${bloomberg.accent.orange}`,
                          }}>{p}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tbl.rows.map((row: any, i: number) => (
                        <tr key={i} style={{
                          background: i % 2 === 0 ? bloomberg.bg.primary : bloomberg.bg.secondary,
                        }}>
                          <td style={{
                            padding: '6px 12px', color: bloomberg.text.secondary,
                            fontWeight: 500, position: 'sticky', left: 0,
                            background: i % 2 === 0 ? bloomberg.bg.primary : bloomberg.bg.secondary,
                            borderBottom: `1px solid ${bloomberg.border.primary}`,
                          }}>{row.metric}</td>
                          {row.values.map((v: any, j: number) => (
                            <td key={j} style={{
                              textAlign: 'right', padding: '6px 12px',
                              fontVariantNumeric: 'tabular-nums', color: bloomberg.text.primary,
                              borderBottom: `1px solid ${bloomberg.border.primary}`,
                            }}>{v ? (currencySymbol !== '$' ? v.replace(/^\$/, currencySymbol) : v) : '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </>
            );
          })() : financials ? (
            <Alert severity="info">Financial statements not available for {sym}</Alert>
          ) : (
            <Typography sx={{ color: bloomberg.text.muted }}>
              Financial data will load when you visit this tab.
            </Typography>
          )}
        </Box>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Tab 3 — Valuation (interactive DCF)                                 */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {tab === 3 && (
        <ValuationTab symbol={sym} currencySymbol={currencySymbol} />
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Tab 4 — Research                                                    */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {tab === 4 && (
        <Box>
          {researchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : researchError ? (
            <Alert severity="error">{researchError}</Alert>
          ) : research ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Toolbar */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small" variant="outlined"
                  startIcon={researchLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                  onClick={() => loadResearch(true)}
                  disabled={researchLoading}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Regenerate Report
                </Button>
              </Box>

              {/* AI Summary */}
              <Box sx={CARD}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>AI Summary</Typography>
                <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.7 }}>
                  {research.ai_summary}
                </Typography>
                {research.key_insights && research.key_insights.length > 0 && (
                  <Box component="ul" sx={{ mt: 1.5, pl: 2, color: bloomberg.text.secondary, fontSize: '0.85rem', mb: 0 }}>
                    {research.key_insights.map((ins, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>{ins}</li>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Price Target visual */}
              {research.price_targets && (() => {
                const pt = research.price_targets!;
                const lo = pt.low ?? pt.mean * 0.85;
                const hi = pt.high ?? pt.mean * 1.15;
                const range = hi - lo;
                const curPct = range > 0 ? clamp01((price - lo) / range) * 100 : 50;
                const meanPct = range > 0 ? clamp01((pt.mean - lo) / range) * 100 : 50;
                const upside = pt.upside ?? (price > 0 ? ((pt.mean - price) / price) * 100 : null);
                return (
                  <Box sx={CARD}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Price Target</Typography>
                    <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Mean Target</Typography>
                        <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: '1.1rem' }}>
                          {currencySymbol}{pt.mean.toFixed(2)}
                        </Typography>
                      </Box>
                      {upside != null && (
                        <Box>
                          <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Upside</Typography>
                          <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: '1.1rem', color: perfColor(upside) }}>
                            {fmtPerf(upside)}
                          </Typography>
                        </Box>
                      )}
                      {pt.num_analysts && (
                        <Box>
                          <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Analysts</Typography>
                          <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: '1.1rem' }}>
                            {pt.num_analysts}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ position: 'relative', height: 8, bgcolor: bloomberg.bg.tertiary, borderRadius: 4, mb: 0.5 }}>
                      <Box sx={{
                        position: 'absolute', left: 0, top: 0, height: '100%',
                        width: `${meanPct}%`, bgcolor: `${bloomberg.accent.orange}55`, borderRadius: 4,
                      }} />
                      {/* Mean marker (orange) */}
                      <Box sx={{
                        position: 'absolute', left: `${meanPct}%`, top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 14, height: 14, borderRadius: '50%',
                        bgcolor: bloomberg.accent.orange, border: '2px solid #000', zIndex: 2,
                      }} />
                      {/* Current price marker (white) */}
                      <Box sx={{
                        position: 'absolute', left: `${curPct}%`, top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 10, height: 10, borderRadius: '50%',
                        bgcolor: '#e2e8f0', border: '2px solid #000', zIndex: 3,
                      }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Low {currencySymbol}{lo.toFixed(2)}</Typography>
                      <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>High {currencySymbol}{hi.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                );
              })()}

              {/* Analyst breakdown */}
              {research.analyst_recommendations?.has_data && (() => {
                const ar = research.analyst_recommendations!;
                const bd = ar.breakdown;
                const total = bd.strong_buy + bd.buy + bd.hold + bd.sell + bd.strong_sell || 1;
                return (
                  <Box sx={CARD}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle1">Analyst Ratings</Typography>
                      {ar.consensus && (
                        <Chip label={ar.consensus} size="small"
                          sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 700 }} />
                      )}
                    </Box>
                    {[
                      { label: 'Strong Buy', count: bd.strong_buy, color: '#16a34a' },
                      { label: 'Buy', count: bd.buy, color: '#22c55e' },
                      { label: 'Hold', count: bd.hold, color: '#f59e0b' },
                      { label: 'Sell', count: bd.sell, color: '#ef4444' },
                      { label: 'Strong Sell', count: bd.strong_sell, color: '#dc2626' },
                    ].map(({ label, count, color }) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.78rem', width: 90, color: bloomberg.text.secondary }}>{label}</Typography>
                        <Box sx={{ flex: 1, height: 8, bgcolor: bloomberg.bg.tertiary, borderRadius: 2, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(count / total) * 100}%`, bgcolor: color, borderRadius: 2 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.78rem', width: 20, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {count}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {/* Comparable Companies */}
              {(research.cfa_analysis?.peer_comparison?.peers?.length ?? 0) > 0 && (
                <Box sx={CARD}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Comparable Companies</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr>
                          {['Company', 'P/E', 'EV/EBITDA', 'P/B', 'P/S'].map(h => (
                            <th key={h} style={{
                              textAlign: h === 'Company' ? 'left' : 'right',
                              padding: '8px 12px', background: bloomberg.bg.tertiary,
                              color: bloomberg.accent.orange, fontWeight: 700,
                              fontSize: '0.7rem', textTransform: 'uppercase',
                              borderBottom: `2px solid ${bloomberg.accent.orange}`,
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {research.cfa_analysis!.peer_comparison.peers.map((peer, i) => (
                          <tr key={i}>
                            <td style={{ padding: '6px 12px', borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                              {peer.name}
                            </td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', fontVariantNumeric: 'tabular-nums', borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                              {peer.pe_ratio > 0 ? peer.pe_ratio.toFixed(1) : '—'}
                            </td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', fontVariantNumeric: 'tabular-nums', borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                              {peer.ev_ebitda > 0 ? peer.ev_ebitda.toFixed(1) : '—'}
                            </td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', fontVariantNumeric: 'tabular-nums', borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                              {peer.price_book > 0 ? peer.price_book.toFixed(1) : '—'}
                            </td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', fontVariantNumeric: 'tabular-nums', borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                              {peer.sales_book > 0 ? peer.sales_book.toFixed(1) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  onClick={() => navigate(`/research?symbol=${sym}`)}
                >
                  Open Full Report
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
              <Typography sx={{ color: bloomberg.text.secondary, fontSize: '0.9rem' }}>
                AI research reports use Perplexity credits.
              </Typography>
              <Button
                variant="contained"
                onClick={() => loadResearch()}
                sx={{ bgcolor: bloomberg.accent.orange, color: '#000', fontWeight: 700, '&:hover': { bgcolor: bloomberg.accent.orangeLight ?? bloomberg.accent.orange } }}
              >
                Generate Report
              </Button>
              <Typography sx={{ color: bloomberg.text.muted, fontSize: '0.75rem' }}>
                ~$0.10–$0.15 · cached for 90 days
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Tab 5 — News                                                        */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {tab === 5 && (
        <Box>
          {newsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : news.length === 0 ? (
            <Alert severity="info">No news available for {sym}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {news.map((item, i) => {
                const sourceColors: Record<string, string> = {
                  'Reddit': '#ff4500', 'SEC EDGAR': '#1a237e', 'CNBC': '#005594',
                  'Reuters': '#ff8000', 'Yahoo Finance': '#6001d2', 'Seeking Alpha': '#f28500',
                };
                const sourceColor = Object.entries(sourceColors).find(([k]) => item.source?.includes(k))?.[1] || bloomberg.accent.orange;
                const timeAgo = (() => {
                  const diff = Math.floor((Date.now() - new Date(item.published_at).getTime()) / 60000);
                  if (diff < 1) return 'Just now';
                  if (diff < 60) return `${diff}m ago`;
                  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
                  return `${Math.floor(diff / 1440)}d ago`;
                })();
                return (
                  <Box
                    key={i}
                    component="a"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block', textDecoration: 'none',
                      bgcolor: bloomberg.bg.secondary,
                      border: `1px solid ${bloomberg.border.primary}`,
                      borderLeft: `3px solid ${sourceColor}`,
                      borderRadius: 1, p: 2,
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: bloomberg.accent.orange,
                        bgcolor: bloomberg.bg.tertiary,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip label={item.source} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: sourceColor, color: '#fff' }} />
                      {item.category && (
                        <Chip label={item.category} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                      {item.sentiment && item.sentiment !== 'neutral' && (
                        <Chip
                          label={item.sentiment}
                          size="small"
                          color={item.sentiment === 'positive' ? 'success' : item.sentiment === 'negative' ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem' }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: bloomberg.text.primary, mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    {item.summary && (
                      <Typography sx={{ fontSize: '0.75rem', color: bloomberg.text.secondary, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {item.summary}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>
                      {timeAgo}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
      </Box>
    </Box>
  );
}
